/**
 * Validation Schemas for Forms
 * Using Zod for type-safe validation
 * Senior Developer Best Practices:
 * - Type-safe validation
 * - Reusable schemas
 * - Clear error messages
 * - Business logic validation
 */

import { z } from 'zod';

/**
 * US State Codes - Valid 2-letter state abbreviations
 */
const US_STATE_CODES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
  'PR',
  'VI',
  'AS',
  'GU',
  'MP',
];

/**
 * Organization Form Validation Schema
 * Validates all fields with specific business rules
 */
export const organizationFormSchema = z.object({
  organizationName: z
    .string()
    .min(2, 'Organization name must be at least 2 characters')
    .max(100, 'Organization name must not exceed 100 characters')
    .regex(
      /^[a-zA-Z0-9\s&.,'-]+$/,
      "Organization name can only contain letters, numbers, spaces, and: & . , ' -",
    )
    .refine((val) => val.trim().length >= 2, 'Organization name cannot be only spaces'),

  industry: z
    .string()
    .min(1, 'Please select an industry')
    .refine(
      (val) =>
        [
          'healthcare',
          'education',
          'retail',
          'hospitality',
          'manufacturing',
          'office',
          'other',
        ].includes(val),
      'Please select a valid industry',
    ),

  street: z
    .string()
    .min(5, 'Street address must be at least 5 characters')
    .max(200, 'Street address must not exceed 200 characters')
    .regex(/^[a-zA-Z0-9\s#.,'-]+$/, 'Street address contains invalid characters')
    .refine((val) => val.trim().length >= 5, 'Street address cannot be only spaces'),

  city: z
    .string()
    .min(2, 'City name must be at least 2 characters')
    .max(50, 'City name must not exceed 50 characters')
    .regex(
      /^[a-zA-Z\s'-]+$/,
      'City name can only contain letters, spaces, hyphens, and apostrophes',
    )
    .refine((val) => val.trim().length >= 2, 'City name cannot be only spaces'),

  state: z
    .string()
    .length(2, 'State must be exactly 2 letters')
    .regex(/^[A-Z]{2}$/, 'State must be 2 uppercase letters (e.g., TX, CA, NY)')
    .refine(
      (val) => US_STATE_CODES.includes(val.toUpperCase()),
      'Please enter a valid US state code',
    )
    .transform((val) => val.toUpperCase()),

  zipCode: z
    .string()
    .regex(
      /^\d{5}(-\d{4})?$/,
      'Zip code must be 5 digits or 5+4 format (e.g., 75201 or 75201-1234)',
    )
    .refine((val) => {
      // Additional validation: first 5 digits should be valid US ZIP range
      const zip5 = val.split('-')[0];
      const zipNum = parseInt(zip5, 10);
      return zipNum >= 1000 && zipNum <= 99999;
    }, 'Please enter a valid US zip code'),
});

/**
 * Helper function to validate organization form data
 * @param {object} data - Form data to validate
 * @returns {object} - { success: boolean, errors: object, data: object }
 */
export const validateOrganizationForm = (data) => {
  try {
    const validated = organizationFormSchema.parse(data);
    return { success: true, errors: {}, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = {};
      error.errors.forEach((err) => {
        const field = err.path[0];
        errors[field] = err.message;
      });
      return { success: false, errors, data: null };
    }
    return { success: false, errors: { general: 'Validation failed' }, data: null };
  }
};

/**
 * Primary Administrator Form Validation Schema
 * Validates administrator contact information
 */
export const administratorFormSchema = z.object({
  fullName: z
    .string()
    .min(2, 'Full name must be at least 2 characters')
    .max(100, 'Full name must not exceed 100 characters')
    .regex(
      /^[a-zA-Z\s'-]+$/,
      'Full name can only contain letters, spaces, hyphens, and apostrophes',
    )
    .refine((val) => val.trim().length >= 2, 'Full name cannot be only spaces'),

  phoneNumber: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.trim().length === 0 || /^\+?[\d\s\-().]{7,20}$/.test(val),
      'Phone number must be 7-20 digits with optional +, spaces, hyphens, parentheses, or periods',
    ),

  emailAddress: z
    .string()
    .min(1, 'Email address is required')
    .email('Please enter a valid email address (e.g., name@example.com)')
    .max(255, 'Email address must not exceed 255 characters')
    .refine(
      (val) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val),
      'Email must have a valid format (e.g., user@domain.com)',
    ),
});

/**
 * Helper function to validate administrator form data
 * @param {object} data - Form data to validate
 * @returns {object} - { success: boolean, errors: object, data: object }
 */
export const validateAdministratorForm = (data) => {
  try {
    const validated = administratorFormSchema.parse(data);
    return { success: true, errors: {}, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = {};
      error.errors.forEach((err) => {
        const field = err.path[0];
        errors[field] = err.message;
      });
      return { success: false, errors, data: null };
    }
    return { success: false, errors: { general: 'Validation failed' }, data: null };
  }
};

/**
 * Field-level validation functions for real-time validation
 */
export const fieldValidators = {
  organizationName: (value) => {
    if (!value || value.trim().length === 0) {
      return 'Organization name is required';
    }
    if (value.trim().length < 2) {
      return 'Organization name must be at least 2 characters';
    }
    if (value.length > 100) {
      return 'Organization name must not exceed 100 characters';
    }
    if (!/^[a-zA-Z0-9\s&.,'-]+$/.test(value)) {
      return 'Organization name contains invalid characters';
    }
    return null;
  },

  industry: (value, industryOptions) => {
    if (!value || value.trim().length === 0) {
      return 'Please select an industry';
    }
    const validValues = industryOptions?.map((opt) => opt.value) || [];
    if (!validValues.includes(value)) {
      return 'Please select a valid industry';
    }
    return null;
  },

  street: (value) => {
    if (!value || value.trim().length === 0) {
      return 'Street address is required';
    }
    if (value.trim().length < 5) {
      return 'Street address must be at least 5 characters';
    }
    if (value.length > 200) {
      return 'Street address must not exceed 200 characters';
    }
    if (!/^[a-zA-Z0-9\s#.,'-]+$/.test(value)) {
      return 'Street address contains invalid characters';
    }
    return null;
  },

  city: (value) => {
    if (!value || value.trim().length === 0) {
      return 'City is required';
    }
    if (value.trim().length < 2) {
      return 'City name must be at least 2 characters';
    }
    if (value.length > 50) {
      return 'City name must not exceed 50 characters';
    }
    if (!/^[a-zA-Z\s'-]+$/.test(value)) {
      return 'City name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return null;
  },

  state: (value) => {
    if (!value || value.trim().length === 0) {
      return 'State is required';
    }
    const upperValue = value.toUpperCase().trim();
    if (upperValue.length !== 2) {
      return 'State must be exactly 2 letters';
    }
    if (!/^[A-Z]{2}$/.test(upperValue)) {
      return 'State must be 2 uppercase letters (e.g., TX, CA, NY)';
    }
    if (!US_STATE_CODES.includes(upperValue)) {
      return 'Please enter a valid US state code';
    }
    return null;
  },

  zipCode: (value) => {
    if (!value || value.trim().length === 0) {
      return 'Zip code is required';
    }
    if (!/^\d{5}(-\d{4})?$/.test(value)) {
      return 'Zip code must be 5 digits or 5+4 format (e.g., 75201 or 75201-1234)';
    }
    const zip5 = value.split('-')[0];
    const zipNum = parseInt(zip5, 10);
    if (zipNum < 1000 || zipNum > 99999) {
      return 'Please enter a valid US zip code';
    }
    return null;
  },

  // Facility form validators
  facilityName: (value) => {
    if (!value || value.trim().length === 0) {
      return 'Facility name is required';
    }
    if (value.trim().length < 2) {
      return 'Facility name must be at least 2 characters';
    }
    if (value.length > 100) {
      return 'Facility name must not exceed 100 characters';
    }
    if (!/^[a-zA-Z0-9\s&.,'-]+$/.test(value)) {
      return 'Facility name contains invalid characters';
    }
    return null;
  },

  // Administrator form validators
  fullName: (value) => {
    if (!value || value.trim().length === 0) {
      return 'Full name is required';
    }
    if (value.trim().length < 2) {
      return 'Full name must be at least 2 characters';
    }
    if (value.length > 100) {
      return 'Full name must not exceed 100 characters';
    }
    if (!/^[a-zA-Z\s'-]+$/.test(value)) {
      return 'Full name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return null;
  },

  phoneNumber: (value) => {
    if (!value || value.trim().length === 0) {
      return 'Phone number is required';
    }
    if (!/^\+?[\d\s\-().]{7,20}$/.test(value)) {
      return 'Phone number must be 7-20 digits with optional +, spaces, hyphens, parentheses, or periods';
    }
    return null;
  },

  emailAddress: (value) => {
    if (!value || value.trim().length === 0) {
      return 'Email address is required';
    }
    if (value.length > 255) {
      return 'Email address must not exceed 255 characters';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      return 'Please enter a valid email address (e.g., name@example.com)';
    }
    return null;
  },
};
